import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDateTime } from "@/lib/utils-ui";
import { toast } from "sonner";
import { History, ArrowRight, Package, RotateCcw, UserPlus2 } from "lucide-react";

const ACTION_META = {
  created:           { color: "#888",    label: "CREATED",    icon: Package },
  assigned:          { color: "#1F6FEB", label: "ASSIGNED",   icon: UserPlus2 },
  reassigned:        { color: "#FF7A00", label: "REASSIGNED", icon: ArrowRight },
  returned_to_store: { color: "#0a7a3f", label: "RETURNED",   icon: RotateCcw },
  status_changed:    { color: "#444",    label: "STATUS",     icon: History },
};

export default function AssetHistoryModal({ asset, open, onOpenChange, users = [], onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("history");
  const [assignForm, setAssignForm] = useState({ user_id: "", department: "", remarks: "" });
  const [returnForm, setReturnForm] = useState({ location: "", returned_condition: "good", remarks: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && asset?.id) {
      api.get(`/assets/${asset.id}/history`).then(r => setData(r.data));
      setReturnForm({ location: asset?.location || "", returned_condition: "good", remarks: "" });
      setAssignForm({ user_id: "", department: "", remarks: "" });
      setTab("history");
    }
  }, [open, asset?.id]); // eslint-disable-line

  const canManage = ["super_admin","company_admin","it_admin"].includes(user?.role);

  const userName = (id) => users.find(u => u.id === id)?.full_name || (id ? id.slice(0, 8) : "—");
  const currentOwner = users.find(u => u.id === asset?.assigned_user_id);

  const doAssign = async () => {
    if (!assignForm.user_id) { toast.error("Pick a user"); return; }
    setLoading(true);
    try {
      await api.post(`/assets/${asset.id}/assign`, assignForm);
      toast.success(asset?.assigned_user_id ? "Asset reassigned" : "Asset assigned");
      const r = await api.get(`/assets/${asset.id}/history`); setData(r.data);
      onChanged?.();
      setTab("history");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const doReturn = async () => {
    if (!returnForm.location) { toast.error("Store location required"); return; }
    setLoading(true);
    try {
      await api.post(`/assets/${asset.id}/return`, returnForm);
      toast.success("Returned to store");
      const r = await api.get(`/assets/${asset.id}/history`); setData(r.data);
      onChanged?.();
      setTab("history");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-head uppercase tracking-tight">
            <span className="font-mono">{asset?.asset_id}</span> · Lifecycle
          </DialogTitle>
        </DialogHeader>

        <div className="border border-border bg-[#F9F9F8] p-4 grid grid-cols-3 gap-4">
          <div><DataLabel>Current Owner</DataLabel><div className="font-semibold mt-1">{currentOwner?.full_name || <span className="text-muted-foreground">In store</span>}</div></div>
          <div><DataLabel>Status</DataLabel><div className="font-mono uppercase mt-1">{asset?.status}</div></div>
          <div><DataLabel>Location</DataLabel><div className="font-mono text-xs mt-1">{asset?.location || "—"}</div></div>
        </div>

        {canManage && (
          <div className="flex border border-border" data-testid="lifecycle-tabs">
            {["history","assign","return"].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                      className={`flex-1 px-4 py-2 text-xs uppercase tracking-wider font-head ${tab === t ? "bg-black text-white" : "bg-white hover:bg-muted"}`}>
                {t === "history" ? "History" : t === "assign" ? (asset?.assigned_user_id ? "Reassign" : "Assign") : "Return to store"}
              </button>
            ))}
          </div>
        )}

        {tab === "history" && (
          <div className="max-h-96 overflow-y-auto" data-testid="history-timeline">
            {!data && <div className="data-label p-4">Loading…</div>}
            {data?.history?.length === 0 && <div className="text-xs text-muted-foreground p-6 text-center">No lifecycle events yet.</div>}
            <ol className="relative border-l border-border ml-3">
              {data?.history?.map(h => {
                const meta = ACTION_META[h.action] || { color: "#444", label: h.action.toUpperCase(), icon: History };
                const Icon = meta.icon;
                return (
                  <li key={h.id} className="ml-5 py-3" data-testid={`history-entry-${h.id}`}>
                    <span className="absolute -left-2 w-4 h-4 flex items-center justify-center bg-white border border-black"><Icon size={9} color={meta.color}/></span>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{fmtDateTime(h.timestamp)}</span>
                    </div>
                    <div className="text-sm mt-1 space-y-0.5">
                      {h.from_user_id && <div><span className="data-label">From</span> <span className="ml-1">{userName(h.from_user_id)}</span></div>}
                      {h.to_user_id && <div><span className="data-label">To</span> <span className="ml-1">{userName(h.to_user_id)}</span></div>}
                      {h.department && <div><span className="data-label">Dept</span> <span className="ml-1">{h.department}</span></div>}
                      {h.location && <div><span className="data-label">Location</span> <span className="ml-1 font-mono text-xs">{h.location}</span></div>}
                      {h.returned_condition && <div><span className="data-label">Condition</span> <span className="ml-1 uppercase">{h.returned_condition}</span></div>}
                      {h.remarks && <div className="text-xs text-muted-foreground italic mt-1">"{h.remarks}"</div>}
                      {h.note && !h.remarks && <div className="text-xs text-muted-foreground italic mt-1">{h.note}</div>}
                      <div className="data-label">By {userName(h.changed_by)}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {tab === "assign" && canManage && (
          <div className="space-y-3" data-testid="assign-form">
            <div><Label className="data-label">Assign to user</Label>
              <Select value={assignForm.user_id} onValueChange={(v) => setAssignForm({...assignForm, user_id: v})}>
                <SelectTrigger data-testid="assign-user"><SelectValue placeholder="Select user"/></SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.active && u.company_id === asset?.company_id).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="data-label">Department</Label><Input value={assignForm.department} onChange={e => setAssignForm({...assignForm, department: e.target.value})}/></div>
            <div><Label className="data-label">Remarks</Label><Input value={assignForm.remarks} onChange={e => setAssignForm({...assignForm, remarks: e.target.value})} placeholder="Optional notes"/></div>
            <DialogFooter><Button onClick={doAssign} disabled={loading} data-testid="assign-submit" className="bg-black text-white hover:bg-[#FF5000]">{asset?.assigned_user_id ? "Reassign" : "Assign"}</Button></DialogFooter>
          </div>
        )}

        {tab === "return" && canManage && (
          <div className="space-y-3" data-testid="return-form">
            <div><Label className="data-label">Store Location</Label><Input value={returnForm.location} onChange={e => setReturnForm({...returnForm, location: e.target.value})} placeholder="e.g., HQ Storeroom A"/></div>
            <div><Label className="data-label">Returned Condition</Label>
              <Select value={returnForm.returned_condition} onValueChange={(v) => setReturnForm({...returnForm, returned_condition: v})}>
                <SelectTrigger data-testid="return-condition"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good (back to in_stock)</SelectItem>
                  <SelectItem value="damaged">Damaged → Repair queue</SelectItem>
                  <SelectItem value="scrap">Scrap → Disposed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="data-label">Remarks</Label><Input value={returnForm.remarks} onChange={e => setReturnForm({...returnForm, remarks: e.target.value})} placeholder="What was the condition?"/></div>
            <DialogFooter><Button onClick={doReturn} disabled={loading} data-testid="return-submit" className="bg-black text-white hover:bg-[#FF5000]">Return to store</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
